<template>
  <NuxtImg
    v-if="!useBackgroundFallback"
    v-bind="$attrs"
    :src="src"
    :alt="alt"
    :sizes="sizes || undefined"
    :loading="loadingAttr"
    :fetchpriority="fetchPriorityAttr"
    :preload="preloadAttr"
    decoding="async"
    @load="emitLoaded"
  />
  <div
    v-else
    v-bind="$attrs"
    class="car-preview-img car-preview-img--bg-fallback"
    :style="{ backgroundImage: `url(${src})` }"
  />
</template>

<script setup lang="ts">
import { computed, onMounted, watch, ref } from 'vue';
import { isSafari, isIOSChromeMobile, isInAppBrowser } from '~/utils/device';

const props = defineProps<{
  src: string;
  alt?: string;
  sizes?: string;
  loading?: 'lazy' | 'eager';
  fetchpriority?: 'high' | 'low' | 'auto';
  preload?: boolean;
}>();

const emit = defineEmits<{
  (e: 'loaded'): void;
}>();

const hasLoaded = ref(false);
const emitLoaded = () => {
  if (hasLoaded.value) return;
  hasLoaded.value = true;
  emit('loaded');
};

const useBackgroundFallback = computed(() => {
  if (!process.client) return false;
  return isSafari() || isIOSChromeMobile() || isInAppBrowser();
});
const loadingAttr = computed(() => props.loading || 'lazy');
const fetchPriorityAttr = computed(() => props.fetchpriority ?? 'low');
const preloadAttr = computed(() => props.preload ?? false);

const preloadFallback = () => {
  if (!useBackgroundFallback.value || !props.src) return;
  const img = new Image();
  img.onload = emitLoaded;
  img.src = props.src;
};

onMounted(preloadFallback);
watch(() => props.src, preloadFallback);
</script>
